import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { addMonths, format, parseISO, isBefore, differenceInDays } from 'date-fns';
import AssetModal from '../components/AssetModal';
import MaintenanceAnalytics from '../components/MaintenanceAnalytics';

const { FiPlus, FiHistory, FiTool, FiCheckCircle, FiFilter, FiRefreshCw, FiLayers, FiAlertCircle, FiShield, FiDollarSign, FiSearch, FiX, FiCalendar, FiTrash2, FiClock, FiZap, FiEdit2, FiCheck } = FiIcons;

// ─── Standard Preventative Templates ─────────────────────────────────
const STANDARD_TEMPLATES = [
  { task: 'HVAC Filter Replacement', category: 'HVAC', intervalMonths: 3, estimatedCost: 50, priority: 'Standard' },
  { task: 'HVAC Full Service & Inspection', category: 'HVAC', intervalMonths: 12, estimatedCost: 250, priority: 'Standard' },
  { task: 'Smoke & CO Detector Battery Check', category: 'Safety', intervalMonths: 6, estimatedCost: 25, priority: 'Standard' },
  { task: 'Fire Extinguisher Inspection', category: 'Safety', intervalMonths: 12, estimatedCost: 30, priority: 'Standard' },
  { task: 'Water Heater Flush & Inspection', category: 'Plumbing', intervalMonths: 12, estimatedCost: 150, priority: 'Standard' },
  { task: 'Gutter Cleaning', category: 'Exterior', intervalMonths: 6, estimatedCost: 120, priority: 'Standard' },
  { task: 'Pest Control Treatment', category: 'General', intervalMonths: 3, estimatedCost: 85, priority: 'Standard' },
  { task: 'Dryer Vent Cleaning', category: 'Appliance', intervalMonths: 12, estimatedCost: 100, priority: 'Standard' },
];

const PREFERRED_TEMPLATES = [
  { task: 'Deep Carpet / Floor Cleaning', category: 'Cleaning', intervalMonths: 6, estimatedCost: 200, priority: 'Preferred' },
  { task: 'Window Washing (Interior + Exterior)', category: 'Cleaning', intervalMonths: 6, estimatedCost: 175, priority: 'Preferred' },
  { task: 'Appliance Deep Clean & Maintenance', category: 'Appliance', intervalMonths: 6, estimatedCost: 120, priority: 'Preferred' },
  { task: 'Plumbing System Inspection', category: 'Plumbing', intervalMonths: 12, estimatedCost: 200, priority: 'Preferred' },
  { task: 'Electrical Panel & Outlet Inspection', category: 'Electrical', intervalMonths: 24, estimatedCost: 175, priority: 'Preferred' },
  { task: 'Roof Inspection', category: 'Exterior', intervalMonths: 12, estimatedCost: 300, priority: 'Preferred' },
  { task: 'Exterior Paint / Siding Touch-up', category: 'Exterior', intervalMonths: 24, estimatedCost: 400, priority: 'Preferred' },
  { task: 'Locks & Security Hardware Check', category: 'Security', intervalMonths: 6, estimatedCost: 60, priority: 'Preferred' },
  { task: 'Landscaping & Tree Trimming', category: 'Exterior', intervalMonths: 3, estimatedCost: 150, priority: 'Preferred' },
];

// ─── Work Order Form Modal ──────────────────────────────────────────
const WorkOrderModal = ({ isOpen, onClose, onSave, properties, categories }) => {
  const [form, setForm] = useState({ propertyId: '', issue: '', category: 'General', estimatedCost: '', contractor: '' });
  if (!isOpen) return null;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.propertyId || !form.issue) return;
    onSave({ ...form, estimatedCost: Number(form.estimatedCost) || 0, status: 'Pending', reported: new Date().toISOString().split('T')[0], completedDate: '', actualCost: 0 });
    setForm({ propertyId: '', issue: '', category: 'General', estimatedCost: '', contractor: '' });
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">New Work Order</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><SafeIcon icon={FiX} size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Property *</label>
            <select required value={form.propertyId} onChange={e => setForm({ ...form, propertyId: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
              <option value="">Select property...</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Issue *</label>
            <textarea required value={form.issue} onChange={e => setForm({ ...form, issue: e.target.value })} rows={2} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none" placeholder="Describe the issue..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Est. Cost ($)</label>
              <input type="number" min="0" value={form.estimatedCost} onChange={e => setForm({ ...form, estimatedCost: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="0" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Contractor</label>
            <input value={form.contractor} onChange={e => setForm({ ...form, contractor: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Optional" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
            <button type="submit" className="flex-1 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">Create</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Schedule Edit/Create Modal ──────────────────────────────────────
const ScheduleFormModal = ({ isOpen, onClose, onSave, properties, initialData }) => {
  const categories = ['HVAC', 'Plumbing', 'Electrical', 'Appliance', 'Safety', 'Exterior', 'Cleaning', 'Security', 'General'];
  const defaults = {
    task: '', category: 'General', propertyId: '', intervalMonths: 3,
    estimatedCost: '', nextDue: format(new Date(), 'yyyy-MM-dd'),
    priority: 'Custom', lastCompleted: '', enabled: true
  };
  const [form, setForm] = useState(defaults);

  React.useEffect(() => {
    setForm(initialData ? { ...defaults, ...initialData } : defaults);
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.task || !form.propertyId) return;
    onSave({ ...form, estimatedCost: Number(form.estimatedCost) || 0, intervalMonths: Number(form.intervalMonths) || 3 });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{initialData?.id ? 'Edit Schedule' : 'New Preventative Schedule'}</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><SafeIcon icon={FiX} size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Task Name *</label>
            <input required value={form.task} onChange={e => setForm({ ...form, task: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="e.g. HVAC Filter Replacement" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Property *</label>
              <select required value={form.propertyId} onChange={e => setForm({ ...form, propertyId: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                <option value="">Select property...</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Repeat Every</label>
              <div className="flex items-center gap-1">
                <input type="number" min="1" max="60" value={form.intervalMonths} onChange={e => setForm({ ...form, intervalMonths: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                <span className="text-xs text-gray-400 whitespace-nowrap">months</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Next Due Date *</label>
              <input type="date" required value={form.nextDue} onChange={e => setForm({ ...form, nextDue: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Est. Cost ($)</label>
              <input type="number" min="0" step="0.01" value={form.estimatedCost} onChange={e => setForm({ ...form, estimatedCost: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="0" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
            <button type="submit" className="flex-1 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5">
              <SafeIcon icon={FiCheck} size={14} /> {initialData?.id ? 'Update Schedule' : 'Create Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Preventative Schedule View ──────────────────────────────────────
const PreventativeView = ({ schedules, properties, onAdd, onDelete, onMarkComplete, onUpdate }) => {
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  const today = new Date();

  const schedulesWithStatus = useMemo(() => {
    return schedules.map(s => {
      const nextDue = parseISO(s.nextDue);
      const daysUntil = differenceInDays(nextDue, today);
      let status = 'upcoming';
      if (daysUntil < 0) status = 'overdue';
      else if (daysUntil <= 7) status = 'due-soon';
      return { ...s, daysUntil, status };
    }).sort((a, b) => a.daysUntil - b.daysUntil);
  }, [schedules, today]);

  const overdue = schedulesWithStatus.filter(s => s.status === 'overdue').length;
  const dueSoon = schedulesWithStatus.filter(s => s.status === 'due-soon').length;
  const annualCost = schedules.reduce((sum, s) => sum + ((s.estimatedCost || 0) * (12 / (s.intervalMonths || 12))), 0);

  const addFromTemplate = (template) => {
    if (!selectedProperty) { alert('Please select a property first.'); return; }
    const nextDue = format(addMonths(today, template.intervalMonths), 'yyyy-MM-dd');
    onAdd({
      ...template,
      propertyId: selectedProperty,
      nextDue,
      lastCompleted: '',
      enabled: true,
    });
  };

  const addAllTemplates = (templates) => {
    if (!selectedProperty) { alert('Please select a property first.'); return; }
    templates.forEach(template => {
      const nextDue = format(addMonths(today, template.intervalMonths), 'yyyy-MM-dd');
      onAdd({
        ...template,
        propertyId: selectedProperty,
        nextDue,
        lastCompleted: '',
        enabled: true,
      });
    });
    setShowTemplates(false);
  };

  const handleComplete = (schedule) => {
    const nextDue = format(addMonths(today, schedule.intervalMonths), 'yyyy-MM-dd');
    onMarkComplete(schedule.id, {
      lastCompleted: format(today, 'yyyy-MM-dd'),
      nextDue,
    });
  };

  const allTemplates = [...STANDARD_TEMPLATES, ...PREFERRED_TEMPLATES];
  const filteredTemplates = templateFilter === 'all' ? allTemplates
    : templateFilter === 'standard' ? STANDARD_TEMPLATES : PREFERRED_TEMPLATES;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Active Schedules</p>
          <p className="text-2xl font-bold text-indigo-600">{schedules.length}</p>
        </div>
        <div className={`p-5 rounded-xl border shadow-sm ${overdue > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Overdue</p>
          <p className={`text-2xl font-bold ${overdue > 0 ? 'text-red-600' : 'text-gray-300'}`}>{overdue}</p>
        </div>
        <div className={`p-5 rounded-xl border shadow-sm ${dueSoon > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Due This Week</p>
          <p className={`text-2xl font-bold ${dueSoon > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{dueSoon}</p>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Est. Annual Cost</p>
          <p className="text-2xl font-bold text-gray-900">${Math.round(annualCost).toLocaleString()}</p>
        </div>
      </div>

      {/* Add from Templates */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiShield} size={20} />
            </div>
            <div>
              <h3 className="font-bold text-sm">Preventative Maintenance Templates</h3>
              <p className="text-indigo-200 text-xs mt-0.5">{STANDARD_TEMPLATES.length} standard + {PREFERRED_TEMPLATES.length} preferred schedules available</p>
            </div>
          </div>
          <button onClick={() => setShowTemplates(!showTemplates)}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition-colors">
            {showTemplates ? 'Close' : 'Browse Templates'}
          </button>
        </div>

        {showTemplates && (
          <div className="mt-5 space-y-4">
            <div className="flex flex-col md:flex-row gap-3">
              <select value={selectedProperty} onChange={e => setSelectedProperty(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm outline-none placeholder-indigo-200 [&>option]:text-gray-900">
                <option value="">Select property to apply...</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div className="flex gap-1 bg-white/10 rounded-lg p-0.5">
                {[{ id: 'all', label: 'All' }, { id: 'standard', label: 'Standard' }, { id: 'preferred', label: 'Preferred' }].map(f => (
                  <button key={f.id} onClick={() => setTemplateFilter(f.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${templateFilter === f.id ? 'bg-white text-indigo-700' : 'text-indigo-200 hover:text-white'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto pr-1">
              {filteredTemplates.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-white/10 rounded-lg hover:bg-white/15 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{t.task}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-indigo-200">{t.category}</span>
                      <span className="text-[10px] text-indigo-300">Every {t.intervalMonths}mo</span>
                      <span className="text-[10px] text-indigo-300">~${t.estimatedCost}</span>
                    </div>
                  </div>
                  <button onClick={() => addFromTemplate(t)}
                    className="shrink-0 p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
                    <SafeIcon icon={FiPlus} size={14} />
                  </button>
                </div>
              ))}
            </div>

            {selectedProperty && (
              <div className="flex gap-2 pt-2 border-t border-white/10">
                <button onClick={() => addAllTemplates(STANDARD_TEMPLATES)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition-colors">
                  Add All Standard ({STANDARD_TEMPLATES.length})
                </button>
                <button onClick={() => addAllTemplates(PREFERRED_TEMPLATES)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition-colors">
                  Add All Preferred ({PREFERRED_TEMPLATES.length})
                </button>
                <button onClick={() => addAllTemplates(allTemplates)}
                  className="px-4 py-2 bg-white text-indigo-700 rounded-lg text-xs font-bold transition-colors hover:bg-indigo-50">
                  Add All Templates ({allTemplates.length})
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom schedule button */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-700">Active Schedules</h4>
        <button onClick={() => { setEditingSchedule(null); setIsScheduleModalOpen(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors">
          <SafeIcon icon={FiPlus} size={13} /> New Custom Schedule
        </button>
      </div>

      {/* Active Schedules */}
      {schedulesWithStatus.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <SafeIcon icon={FiCalendar} size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-400">No preventative schedules yet</p>
          <p className="text-xs text-gray-300 mt-1">Use the templates above or create a custom schedule</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Task</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Property</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Frequency</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Next Due</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Est. Cost</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {schedulesWithStatus.map(s => {
                  const prop = properties.find(p => p.id === s.propertyId);
                  return (
                    <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-gray-900">{s.task}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-gray-400">{s.category}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${s.priority === 'Standard' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>{s.priority}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{prop?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm text-gray-600">Every {s.intervalMonths} mo</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            s.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            s.status === 'due-soon' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-50 text-green-600'
                          }`}>
                            {s.status === 'overdue' ? `${Math.abs(s.daysUntil)}d overdue` :
                             s.status === 'due-soon' ? `${s.daysUntil}d` :
                             format(parseISO(s.nextDue), 'MMM d, yyyy')}
                          </span>
                        </div>
                        {s.lastCompleted && <p className="text-[10px] text-gray-400 mt-0.5">Last: {s.lastCompleted}</p>}
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-gray-700">${s.estimatedCost}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleComplete(s)} title="Mark Complete & Reschedule"
                            className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors">
                            <SafeIcon icon={FiCheckCircle} size={16} />
                          </button>
                          <button onClick={() => { setEditingSchedule(s); setIsScheduleModalOpen(true); }} title="Edit Schedule"
                            className="p-1.5 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors">
                            <SafeIcon icon={FiEdit2} size={15} />
                          </button>
                          <button onClick={() => onDelete(s.id)} title="Remove Schedule"
                            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                            <SafeIcon icon={FiTrash2} size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ScheduleFormModal
        isOpen={isScheduleModalOpen}
        onClose={() => { setIsScheduleModalOpen(false); setEditingSchedule(null); }}
        onSave={(scheduleData) => {
          if (editingSchedule?.id) {
            onUpdate(editingSchedule.id, scheduleData);
          } else {
            onAdd(scheduleData);
          }
        }}
        properties={properties}
        initialData={editingSchedule}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
const Maintenance = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [filterProp, setFilterProp] = useState('all');
  const [view, setView] = useState('active');
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isWorkOrderOpen, setIsWorkOrderOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['Plumbing', 'Electrical', 'HVAC', 'Appliance', 'Technology', 'Access', 'General'];

  const handleStatusChange = (id, newStatus) => {
    const update = { status: newStatus };
    if (newStatus === 'Completed') update.completedDate = new Date().toISOString().split('T')[0];
    updateRecord('maintenance', id, update);
  };

  const getAssetStatus = (asset) => {
    const today = new Date();
    if (asset.warrantyExpiry && isBefore(parseISO(asset.warrantyExpiry), today)) return 'Out of Warranty';
    if (asset.lastServiceDate && asset.serviceIntervalMonths) {
      const nextService = addMonths(parseISO(asset.lastServiceDate), asset.serviceIntervalMonths);
      if (isBefore(nextService, today)) return 'Service Overdue';
    }
    return 'Healthy';
  };

  const filteredItems = (view === 'assets' ? data.assets : data.maintenance).filter(m => {
    const matchesProp = filterProp === 'all' || m.propertyId === filterProp;
    const searchStr = (m.issue || m.name || '').toLowerCase();
    const matchesSearch = searchStr.includes(searchTerm.toLowerCase());
    if (view === 'assets') return matchesProp && matchesSearch;
    const matchesStatus = view === 'active' ? m.status !== 'Completed' : m.status === 'Completed';
    return matchesProp && matchesStatus && matchesSearch;
  });

  const totalHistorySpend = data.maintenance.filter(m => m.status === 'Completed').reduce((sum, m) => sum + (m.actualCost || 0), 0);
  const schedules = data.preventativeSchedules || [];
  const overdueSchedules = schedules.filter(s => {
    try { return differenceInDays(parseISO(s.nextDue), new Date()) < 0; } catch { return false; }
  }).length;

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Maintenance IQ</h2>
          <p className="text-sm text-gray-500">Asset lifecycle management and repair audit trail.</p>
        </div>
        <div className="flex items-center gap-3">
          {view === 'assets' && (
            <button onClick={() => { setEditingAsset(null); setIsAssetModalOpen(true); }}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm font-semibold text-xs uppercase tracking-wider">
              <SafeIcon icon={FiPlus} className="mr-1.5" size={14} /> Register Asset
            </button>
          )}
          {view === 'active' && (
            <button onClick={() => setIsWorkOrderOpen(true)}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm font-semibold text-xs uppercase tracking-wider">
              <SafeIcon icon={FiPlus} className="mr-1.5" size={14} /> New Work Order
            </button>
          )}
          <div className="flex bg-white rounded-xl p-1 border border-gray-100 shadow-sm">
            {[
              { id: 'active', label: 'Work Orders', icon: FiTool },
              { id: 'assets', label: 'Asset Bank', icon: FiLayers },
              { id: 'preventative', label: 'Preventative', icon: FiShield },
              { id: 'history', label: 'Audit', icon: FiHistory }
            ].map(tab => (
              <button key={tab.id} onClick={() => setView(tab.id)}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-tight rounded-lg transition-all flex items-center gap-1.5 ${view === tab.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                <SafeIcon icon={tab.icon} size={13} /> {tab.label}
                {tab.id === 'preventative' && overdueSchedules > 0 && (
                  <span className="ml-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">{overdueSchedules}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'history' && <MaintenanceAnalytics maintenance={data.maintenance} />}

      {view === 'preventative' ? (
        <PreventativeView
          schedules={schedules}
          properties={data.properties}
          onAdd={(schedule) => addRecord('preventativeSchedules', schedule)}
          onDelete={(id) => deleteRecord('preventativeSchedules', id)}
          onMarkComplete={(id, update) => updateRecord('preventativeSchedules', id, update)}
          onUpdate={(id, update) => updateRecord('preventativeSchedules', id, update)}
        />
      ) : (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
              <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Pending Repairs</p>
              <p className="text-2xl font-bold text-amber-600">{data.maintenance.filter(m => m.status !== 'Completed').length}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
              <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Total Spend</p>
              <p className="text-2xl font-bold text-gray-900">${totalHistorySpend.toLocaleString()}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
              <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Asset Health</p>
              <p className="text-2xl font-bold text-green-600">
                {data.assets.filter(a => getAssetStatus(a) === 'Healthy').length}/{data.assets.length}
              </p>
            </div>
            <button onClick={() => setView('preventative')}
              className="bg-indigo-600 p-5 rounded-xl shadow-lg text-white flex items-center justify-between group overflow-hidden relative hover:bg-indigo-700 transition-colors text-left">
              <div className="relative z-10">
                <h3 className="font-bold text-base leading-tight">Preventative</h3>
                <p className="text-[10px] text-indigo-200 font-semibold mt-0.5">
                  {schedules.length} schedule{schedules.length !== 1 ? 's' : ''} active
                  {overdueSchedules > 0 && <span className="text-red-300 ml-1">({overdueSchedules} overdue)</span>}
                </p>
              </div>
              <SafeIcon icon={FiShield} size={40} className="absolute -bottom-1 -right-1 text-white/10 group-hover:scale-110 transition-transform" />
            </button>
          </div>

          {/* Search & filter */}
          <div className="flex flex-col md:flex-row gap-3 bg-white p-3 rounded-xl shadow-sm border border-gray-100">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input type="text" placeholder={`Search ${view === 'assets' ? 'assets' : 'work orders'}...`}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <select className="bg-gray-50 border-none rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 outline-none focus:ring-2 focus:ring-indigo-500"
              value={filterProp} onChange={e => setFilterProp(e.target.value)}>
              <option value="all">All Properties</option>
              {data.properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Property & Item</th>
                  {view === 'assets' ? (
                    <>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Specs</th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Warranty</th>
                      <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase">Health</th>
                    </>
                  ) : (
                    <>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Category</th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase">Timeline</th>
                      <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase">Cost / Status</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredItems.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-gray-400">No items found.</td></tr>
                ) : filteredItems.map(item => {
                  const prop = data.properties.find(p => p.id === item.propertyId);
                  if (view === 'assets') {
                    const status = getAssetStatus(item);
                    return (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => { setEditingAsset(item); setIsAssetModalOpen(true); }}>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                          <p className="text-[10px] text-gray-400">{prop?.name}</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-xs text-gray-600">{item.brand} {item.model}</p>
                          {item.serialNumber && <p className="text-[10px] text-gray-400 font-mono mt-0.5">SN: {item.serialNumber}</p>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5">
                            <SafeIcon icon={FiShield} className={status === 'Out of Warranty' ? 'text-red-400' : 'text-green-500'} size={13} />
                            <p className="text-xs text-gray-600">{item.warrantyExpiry ? format(parseISO(item.warrantyExpiry), 'MMM yyyy') : 'N/A'}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${status === 'Healthy' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{status}</span>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-gray-900">{item.issue}</p>
                        <p className="text-[10px] text-gray-400">{prop?.name}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-[10px] font-semibold text-gray-500">{item.category}</span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-[10px] text-gray-400">Reported: {item.reported}</p>
                        {item.completedDate && <p className="text-[10px] text-green-600">Done: {item.completedDate}</p>}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {view === 'history' ? (
                          <p className="text-sm font-semibold text-gray-900">${item.actualCost || 0}</p>
                        ) : (
                          <select className={`text-[10px] font-bold uppercase rounded-lg px-2 py-1 border-none outline-none ${item.status === 'Completed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}
                            value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value)}>
                            <option value="Pending">Pending</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AssetModal isOpen={isAssetModalOpen} onClose={() => setIsAssetModalOpen(false)}
        onSave={(d) => editingAsset ? updateRecord('assets', editingAsset.id, d) : addRecord('assets', d)}
        properties={data.properties} initialData={editingAsset} />
      <WorkOrderModal isOpen={isWorkOrderOpen} onClose={() => setIsWorkOrderOpen(false)}
        onSave={(wo) => addRecord('maintenance', wo)} properties={data.properties} categories={categories} />
    </div>
  );
};

export default Maintenance;
